import { Injectable } from '@angular/core';
import { UrlBuilder } from '../../shared/classes/url-builder';
import { QueryStringParameters } from '../../shared/classes/query-string-parameters';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ApiEndpointsService {
  private baseUrl: string;
  private datahubApiUrl: string;
  private lpBaseUrl: string;
  private navBaseUrl: string;
  private lpDatahubApi: string;
  private datahubApiUrlV2: string;
  private fundSandboxApiUrl: string;
  private teamApiUrl: string;
  constructor() {
    this.baseUrl = environment.useRealAPI
      ? environment.datahubApiUrl
      : environment.baseUrl;
    this.datahubApiUrl = environment.datahubApiUrl;
    this.lpDatahubApi = environment.lpDatahubApi;
    this.datahubApiUrlV2 = environment.datahubApiUrlV2;
    this.lpBaseUrl = environment.useRealAPI
      ? environment.lpDatahubApi
      : environment.baseUrl;
    this.navBaseUrl = environment.navBaseUrl;
    this.fundSandboxApiUrl = environment.fundSandboxApiUrl;
    this.teamApiUrl = environment.teamApiUrl;
  }

  createTeamUrl(action: string): string {
    const urlBuilder: UrlBuilder = new UrlBuilder(this.teamApiUrl, action);
    return urlBuilder.toString();
  }

  createUrlV2(action: string, isMockAPI = false): string {
    const urlBuilder: UrlBuilder = new UrlBuilder(
      isMockAPI ? this.baseUrl : this.datahubApiUrlV2,
      action
    );
    return urlBuilder.toString();
  }

  createUrl(action: string, isMockAPI = false): string {
    const urlBuilder: UrlBuilder = new UrlBuilder(
      isMockAPI ? this.baseUrl : this.datahubApiUrl,
      action
    );
    return urlBuilder.toString();
  }

  createPortfolioCompaniesUrl(action: string): string {
    const env = environment as { portfolioCompaniesApiUrl?: string };
    if (env.portfolioCompaniesApiUrl !== undefined) {
      const base = env.portfolioCompaniesApiUrl.replace(/\/$/, '');
      return base ? [base, action].join('/') : `/${action}`;
    }

    return this.createFundSandboxUrl(action, false);
  }

  createLpUrl(action: string, isMockAPI = false): string {
    const urlBuilder: UrlBuilder = new UrlBuilder(
      isMockAPI ? this.lpBaseUrl : this.lpDatahubApi,
      action
    );
    return urlBuilder.toString();
  }

  createNavUrl(action: string, isMockAPI = false): string {
    const urlBuilder: UrlBuilder = new UrlBuilder(
      isMockAPI ? this.navBaseUrl : this.navBaseUrl,
      action
    );
    return urlBuilder.toString();
  }

  createFundSandboxUrl(action: string, isMockAPI = false): string {
    const urlBuilder: UrlBuilder = new UrlBuilder(
      isMockAPI ? this.baseUrl : this.fundSandboxApiUrl,
      action
    );
    return urlBuilder.toString();
  }

  private createUrlWithQueryParameters(
    action: string,
    queryStringHandler?: (queryStringParameters: QueryStringParameters) => void
  ): string {
    const urlBuilder: UrlBuilder = new UrlBuilder(this.baseUrl, action);
    if (queryStringHandler) {
      queryStringHandler(urlBuilder.queryString);
    }
    return urlBuilder.toString();
  }

  private createUrlWithPathVariables(
    action: string,
    pathVariables: any[] = []
  ): string {
    let encodedPathVariablesUrl = '';

    for (const pathVariable of pathVariables) {
      if (pathVariable !== null) {
        encodedPathVariablesUrl += `/${encodeURIComponent(
          pathVariable.toString()
        )}`;
      }
    }

    const urlBuilder: UrlBuilder = new UrlBuilder(
      this.baseUrl,
      `${action}${encodedPathVariablesUrl}`
    );

    return urlBuilder.toString();
  }

  public getSessionUser(isRefresh: boolean): string {
    return this.createUrl(`session-user?isRefresh=${isRefresh}`, false);
  }

  public postSessionUser(): string {
    return this.createUrl('session-user', false);
  }

  public getBaseData(): string {
    return this.createUrl('base-data', false);
  }

  public aceExchange(): string {
    return this.createTeamUrl('auth/keycloak/exchange');
  }

  public aceRefresh(): string {
    return this.createTeamUrl('auth/refresh');
  }

  public getTenantTheme(tenantId?: string): string {
    const query = tenantId
      ? `?tenantId=${encodeURIComponent(tenantId)}`
      : '';
    return this.createTeamUrl(`tenant/theme${query}`);
  }

  public getInvestorOnboardings(queryParams = ''): string {
    return this.createTeamUrl(`gp-portal/investor-onboardings${queryParams}`);
  }

  public getFundSubscriptions(queryParams = ''): string {
    return this.createTeamUrl(`gp-portal/subscriptions${queryParams}`);
  }

  public getSubscriptionDetails(subscriptionId: string): string {
    return this.createTeamUrl(
      `gp-portal/subscriptions/${encodeURIComponent(subscriptionId)}`
    );
  }

  public getInvestorOnboardingDetail(id: string): string {
    return this.createTeamUrl(
      `gp-portal/investor-onboardings/${encodeURIComponent(id)}`
    );
  }

  public getInvestorUploadedDocuments(id: string): string {
    return this.createTeamUrl(
      `gp-portal/investor-onboardings/${encodeURIComponent(
        id
      )}/uploaded-documents`
    );
  }

  public getInvestorSubscriptionDocuments(id: string): string {
    return this.createTeamUrl(
      `gp-portal/investor-onboardings/${encodeURIComponent(
        id
      )}/subscription-documents`
    );
  }

  public approveInvestorOnboarding(id: string): string {
    return this.createTeamUrl(
      `gp-portal/investor-onboardings/${encodeURIComponent(id)}/approve`
    );
  }

  public requestChangesInvestorOnboarding(id: string): string {
    return this.createTeamUrl(
      `gp-portal/investor-onboardings/${encodeURIComponent(id)}/request-changes`
    );
  }

  public getFundSandboxFunds(): string {
    return this.createFundSandboxUrl('funds', false);
  }

  public getFundSandboxCapitalCalls(fundId: string): string {
    return this.createFundSandboxUrl(
      `funds/capital-calls?fundId=${encodeURIComponent(fundId)}`,
      false
    );
  }

  public getFundSandboxCapitalCall(capitalCallId: string): string {
    return this.createFundSandboxUrl(
      `funds/capital-calls/${encodeURIComponent(capitalCallId)}`,
      false
    );
  }

  public getFundSandboxCapitalCallDispatch(capitalCallId: string): string {
    return this.createFundSandboxUrl(
      `funds/capital-calls/${encodeURIComponent(capitalCallId)}/dispatch`,
      false
    );
  }

  public getFundSandboxCapitalCallLpNotice(
    capitalCallId: string,
    lpId: string
  ): string {
    return this.createFundSandboxUrl(
      `funds/capital-calls/${encodeURIComponent(
        capitalCallId
      )}/lps/${encodeURIComponent(lpId)}/notice`,
      false
    );
  }

  public getFundSandboxCapitalCallLpNoticeResend(
    capitalCallId: string,
    lpId: string
  ): string {
    return this.createFundSandboxUrl(
      `funds/capital-calls/${encodeURIComponent(
        capitalCallId
      )}/dispatch/${encodeURIComponent(lpId)}/resend`,
      false
    );
  }

  public getFundSandboxCapitalCallTemplatePreview(fundId: string): string {
    return this.createFundSandboxUrl(
      `data-extractor/fund-profiles/${encodeURIComponent(
        fundId
      )}/capital-call-template/preview`,
      false
    );
  }

  public getFundSandboxCapitalCallDrafts(fundId: string): string {
    return this.createFundSandboxUrl(
      `funds/${encodeURIComponent(fundId)}/capital-call-drafts`,
      false
    );
  }

  public getFundSandboxCapitalCallDraft(
    fundId: string,
    draftId: string
  ): string {
    return this.createFundSandboxUrl(
      `funds/${encodeURIComponent(
        fundId
      )}/capital-call-drafts/${encodeURIComponent(draftId)}`,
      false
    );
  }

  public getFundSandboxCapitalCallPreview(fundId: string): string {
    return this.createFundSandboxUrl(
      `funds/${encodeURIComponent(fundId)}/capital-calls/preview`,
      false
    );
  }

  public getFundSandboxCapitalCallApproval(fundId: string): string {
    return this.createFundSandboxUrl(
      `funds/${encodeURIComponent(fundId)}/capital-calls/approve`,
      false
    );
  }

  public getFundSandboxCapitalCallComments(
    threadType?: 'DRAFT' | 'CAPITAL_CALL',
    threadId?: string
  ): string {
    const query =
      threadType && threadId
        ? `?threadType=${encodeURIComponent(
            threadType
          )}&threadId=${encodeURIComponent(threadId)}`
        : '';
    return this.createFundSandboxUrl(
      `funds/capital-call-comments${query}`,
      false
    );
  }

  public getFundSandboxCapitalCallCommentResolve(messageId: string): string {
    return this.createFundSandboxUrl(
      `funds/capital-call-comments/${encodeURIComponent(messageId)}/resolve`,
      false
    );
  }

  public getFundSandboxRuleSimulation(fundId: string): string {
    return this.createFundSandboxUrl(
      `funds/${encodeURIComponent(fundId)}/simulate-rules`,
      false
    );
  }

  public getFundSandboxLpCommitments(fundId: string): string {
    return this.createFundSandboxUrl(
      `funds/${encodeURIComponent(fundId)}/lps`,
      false
    );
  }

  public getFundSandboxPayments(fundId: string): string {
    return this.createFundSandboxUrl(
      `funds/${encodeURIComponent(fundId)}/payments`,
      false
    );
  }

  public getCurrentUser(): string {
    return this.createTeamUrl('users/me');
  }

  public getTeamMembers(): string {
    return this.createTeamUrl('team/members');
  }

  // Funds live in ACE for the team/staffing view. Cursor-paginated; pass the
  // prior page's nextCursor to walk all pages.
  public getFunds(cursor?: string): string {
    const sizeParam = 'size=100';
    return this.createTeamUrl(
      cursor
        ? `funds?${sizeParam}&cursor=${encodeURIComponent(cursor)}`
        : `funds?${sizeParam}`
    );
  }

  public getCapitalCallForecastBySourceVehicle(
    sourceVehicleId: string
  ): string {
    return this.createTeamUrl(
      `funds/by-source/${encodeURIComponent(
        sourceVehicleId
      )}/capital-call-forecasts/current`
    );
  }

  public getFund(fundId: string): string {
    return this.createTeamUrl(`funds/${encodeURIComponent(fundId)}`);
  }

  public getFundBySource(sourceVehicleId: string): string {
    return this.createTeamUrl(
      `funds/by-source/${encodeURIComponent(sourceVehicleId)}`
    );
  }

  public getFundProspects(fundId: string): string {
    return this.createTeamUrl(`funds/${encodeURIComponent(fundId)}/prospects`);
  }

  public getFundProspectInternalNotes(
    fundId: string,
    prospectId: string
  ): string {
    return this.createTeamUrl(
      `funds/${encodeURIComponent(fundId)}/prospects/${encodeURIComponent(
        prospectId
      )}/internal-notes`
    );
  }

  // The people staffed on a given ACE fund (with their per-fund teamRole).
  public getFundTeam(fundId: string): string {
    return this.createTeamUrl(`funds/${fundId}/team`);
  }

  public getChats(
    params: {
      tenantId?: string;
      fundId?: string;
      archived?: boolean;
    } = {}
  ): string {
    const query = new URLSearchParams();
    if (params.tenantId) query.set('tenantId', params.tenantId);
    if (params.fundId) query.set('fundId', params.fundId);
    if (params.archived !== undefined)
      query.set('archived', String(params.archived));
    const suffix = query.toString();
    return this.createTeamUrl(`chats${suffix ? `?${suffix}` : ''}`);
  }

  public createDirectChat(): string {
    return this.createTeamUrl('chats/direct');
  }

  public createGroupChat(): string {
    return this.createTeamUrl('chats/create-group');
  }

  public createSupportChat(): string {
    return this.createTeamUrl('chats/support');
  }

  public getSpikeSupportChats(
    params: {
      tenantId?: string;
      scope?: 'all' | 'mine' | 'unassigned';
      statuses?: string[];
      fundId?: string;
    } = {}
  ): string {
    const query = new URLSearchParams();
    if (params.tenantId) query.set('tenantId', params.tenantId);
    if (params.scope) query.set('scope', params.scope);
    if (params.statuses?.length)
      query.set('statuses', params.statuses.join(','));
    if (params.fundId) query.set('fundId', params.fundId);
    const suffix = query.toString();
    return this.createTeamUrl(
      `chats/spike-support${suffix ? `?${suffix}` : ''}`
    );
  }

  public getChatUnreadCount(): string {
    return this.createTeamUrl('chats/unread-count');
  }

  public getNotifications(
    params: {
      page?: number;
      size?: number;
      unreadOnly?: boolean;
    } = {}
  ): string {
    const query = new URLSearchParams();
    query.set('page', String(params.page ?? 0));
    query.set('size', String(params.size ?? 20));
    if (params.unreadOnly !== undefined) {
      query.set('unreadOnly', String(params.unreadOnly));
    }
    return this.createTeamUrl(`notifications?${query.toString()}`);
  }

  public getNotificationBadgeCount(): string {
    return this.createTeamUrl('notifications/badge-count');
  }

  public getNotificationUnreadCount(): string {
    return this.createTeamUrl('notifications/unread-count');
  }

  public markAllNotificationsRead(): string {
    return this.createTeamUrl('notifications/read-all');
  }

  public markNotificationRead(id: number): string {
    return this.createTeamUrl(`notifications/${encodeURIComponent(id)}/read`);
  }

  public getChat(chatId: string): string {
    return this.createTeamUrl(`chats/${encodeURIComponent(chatId)}`);
  }

  public updateChat(chatId: string): string {
    return this.createTeamUrl(`chats/${encodeURIComponent(chatId)}`);
  }

  public deleteChat(chatId: string): string {
    return this.createTeamUrl(`chats/${encodeURIComponent(chatId)}`);
  }

  public getChatMessages(
    chatId: string,
    params: { cursor?: string; limit?: number } = {}
  ): string {
    const query = new URLSearchParams();
    if (params.cursor) query.set('cursor', params.cursor);
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    const suffix = query.toString();
    return this.createTeamUrl(
      `chats/${encodeURIComponent(chatId)}/messages${
        suffix ? `?${suffix}` : ''
      }`
    );
  }

  public sendChatMessage(chatId: string): string {
    return this.createTeamUrl(`chats/${encodeURIComponent(chatId)}/messages`);
  }

  public markChatRead(chatId: string): string {
    return this.createTeamUrl(`chats/${encodeURIComponent(chatId)}/read`);
  }

  public markChatUnread(chatId: string): string {
    return this.createTeamUrl(`chats/${encodeURIComponent(chatId)}/unread`);
  }

  public getSupportWorkflow(chatId: string): string {
    return this.createTeamUrl(
      `chats/${encodeURIComponent(chatId)}/support-workflow`
    );
  }

  public getEligibleSupportOwners(chatId: string): string {
    return this.createTeamUrl(
      `chats/${encodeURIComponent(chatId)}/support-workflow/eligible-owners`
    );
  }

  public getSupportWorkflowEvents(chatId: string): string {
    return this.createTeamUrl(
      `chats/${encodeURIComponent(chatId)}/support-workflow/events`
    );
  }

  public claimSupportWorkflow(chatId: string): string {
    return this.createTeamUrl(
      `chats/${encodeURIComponent(chatId)}/support-workflow/claim`
    );
  }

  public assignSupportWorkflow(chatId: string): string {
    return this.createTeamUrl(
      `chats/${encodeURIComponent(chatId)}/support-workflow/owner`
    );
  }

  public updateSupportWorkflowStatus(chatId: string): string {
    return this.createTeamUrl(
      `chats/${encodeURIComponent(chatId)}/support-workflow/status`
    );
  }

  public editChatMessage(chatId: string, messageId: string): string {
    return this.createTeamUrl(
      `chats/${encodeURIComponent(chatId)}/messages/${encodeURIComponent(
        messageId
      )}`
    );
  }

  public setChatMessageReaction(chatId: string, messageId: string): string {
    return this.createTeamUrl(
      `chats/${encodeURIComponent(chatId)}/messages/${encodeURIComponent(
        messageId
      )}/reaction`
    );
  }

  public pinChatMessage(chatId: string, messageId: string): string {
    return this.createTeamUrl(
      `chats/${encodeURIComponent(chatId)}/messages/${encodeURIComponent(
        messageId
      )}/pin`
    );
  }

  public deleteChatMessage(chatId: string, messageId: string): string {
    return this.createTeamUrl(
      `chats/${encodeURIComponent(chatId)}/messages/${encodeURIComponent(
        messageId
      )}`
    );
  }

  public muteChat(chatId: string): string {
    return this.createTeamUrl(`chats/${encodeURIComponent(chatId)}/mute`);
  }

  public archiveChat(chatId: string): string {
    return this.createTeamUrl(`chats/${encodeURIComponent(chatId)}/archive`);
  }

  public leaveChat(chatId: string): string {
    return this.createTeamUrl(`chats/${encodeURIComponent(chatId)}/leave`);
  }

  public restoreChat(chatId: string): string {
    return this.createTeamUrl(`chats/${encodeURIComponent(chatId)}/restore`);
  }

  public inviteChatMembers(chatId: string): string {
    return this.createTeamUrl(`chats/${encodeURIComponent(chatId)}/members`);
  }

  public removeChatMember(chatId: string, userId: string): string {
    return this.createTeamUrl(
      `chats/${encodeURIComponent(chatId)}/members/${encodeURIComponent(
        userId
      )}`
    );
  }

  public uploadChatFile(chatId: string): string {
    return this.createTeamUrl(
      `chats/${encodeURIComponent(chatId)}/files/upload`
    );
  }

  public downloadChatFile(chatId: string): string {
    return this.createTeamUrl(
      `chats/${encodeURIComponent(chatId)}/files/download`
    );
  }

  // Activity feed for a member. Enum values (tab/category) must be lowercase.
  // Use limit=1 for "last activity"; pass the prior page's nextCursor to walk
  // more. The response is wrapped in an AceEnvelope.
  public getActivity(params: {
    userId: string;
    tab?: string;
    category?: string;
    limit?: number;
    cursor?: string;
  }): string {
    const qs = new URLSearchParams();
    qs.set('userId', params.userId);
    qs.set('tab', params.tab ?? 'member');
    if (params.category) {
      qs.set('category', params.category);
    }
    qs.set('limit', String(params.limit ?? 20));
    if (params.cursor) {
      qs.set('cursor', params.cursor);
    }
    return this.createTeamUrl(`activity?${qs.toString()}`);
  }

  public getFundActivity(params: {
    fundId: string;
    limit?: number;
    cursor?: string;
  }): string {
    const qs = new URLSearchParams();
    qs.set('fundId', params.fundId);
    qs.set('tab', 'member');
    qs.set('limit', String(params.limit ?? 20));
    if (params.cursor) {
      qs.set('cursor', params.cursor);
    }
    return this.createTeamUrl(`activity?${qs.toString()}`);
  }

  public getTasks(
    params: {
      fundId?: string;
      limit?: number;
      cursor?: string;
      search?: string;
      tab?: string;
      source?: 'native' | 'clickup';
      priority?: string[];
      status?: string[];
      scope?: string;
      mine?: boolean;
      memberIds?: string[];
      dueFrom?: string;
      dueTo?: string;
      includePastDue?: boolean;
      atRisk?: boolean;
      tenantId?: string;
    } = {}
  ): string {
    const qs = new URLSearchParams();
    qs.set('limit', String(params.limit ?? 100));
    if (params.fundId) {
      qs.set('fundId', params.fundId);
    }
    if (params.cursor) {
      qs.set('cursor', params.cursor);
    }
    if (params.search) {
      qs.set('search', params.search);
    }
    if (params.tab) {
      qs.set('tab', params.tab);
    }
    if (params.source) {
      qs.set('source', params.source);
    }
    params.priority?.forEach(priority => qs.append('priority', priority));
    params.status?.forEach(status => qs.append('status', status));
    if (params.scope) {
      qs.set('scope', params.scope);
    }
    if (params.mine !== undefined) {
      qs.set('mine', String(params.mine));
    }
    params.memberIds?.forEach(memberId => qs.append('memberIds', memberId));
    if (params.dueFrom) {
      qs.set('dueFrom', params.dueFrom);
    }
    if (params.dueTo) {
      qs.set('dueTo', params.dueTo);
    }
    if (params.includePastDue !== undefined) {
      qs.set('includePastDue', String(params.includePastDue));
    }
    if (params.atRisk !== undefined) {
      qs.set('atRisk', String(params.atRisk));
    }
    if (params.tenantId) {
      qs.set('tenantId', params.tenantId);
    }
    return this.createTeamUrl(`tasks?${qs.toString()}`);
  }

  public getTask(taskId: string): string {
    return this.createTeamUrl(`tasks/${encodeURIComponent(taskId)}`);
  }

  public createTaskComment(taskId: string): string {
    return this.createTeamUrl(`tasks/${encodeURIComponent(taskId)}/comments`);
  }

  public createTask(): string {
    return this.createTeamUrl('tasks');
  }

  public updateTask(taskId: string): string {
    return this.createTeamUrl(`tasks/${encodeURIComponent(taskId)}`);
  }

  public assignTask(taskId: string): string {
    return this.createTeamUrl(`tasks/${encodeURIComponent(taskId)}/assignees`);
  }

  public retryClickUpTaskSync(taskId: string): string {
    return this.createTeamUrl(
      `tasks/${encodeURIComponent(taskId)}/clickup/retry`
    );
  }

  public refreshClickUpTask(taskId: string): string {
    return this.createTeamUrl(
      `tasks/${encodeURIComponent(taskId)}/clickup/refresh`
    );
  }

  public updateTaskFiles(taskId: string): string {
    return this.createTeamUrl(`tasks/${encodeURIComponent(taskId)}/file`);
  }

  public presignUpload(): string {
    return this.createTeamUrl('uploads/presign');
  }

  public deleteTask(taskId: string): string {
    return this.createTeamUrl(`tasks/${encodeURIComponent(taskId)}`);
  }

  public getDashboardData(vehicleId: string): string {
    return this.createLpUrl(`vehicles/${vehicleId}/dashboard`, false);
  }

  public getGpDashboard(params: {
    fundId: string;
    tenantId?: string;
    priorityLimit?: number;
  }): string {
    const query = new URLSearchParams();
    query.set('fundId', params.fundId);
    if (params.tenantId) query.set('tenantId', params.tenantId);
    if (params.priorityLimit !== undefined) {
      query.set('priorityLimit', String(params.priorityLimit));
    }
    return this.createTeamUrl(`dashboard/gp?${query.toString()}`);
  }

  public getDashboardFeed(params: {
    fundId: string;
    tenantId?: string;
    filter?: string;
    limit?: number;
  }): string {
    const query = new URLSearchParams();
    query.set('fundId', params.fundId);
    if (params.tenantId) query.set('tenantId', params.tenantId);
    if (params.filter) query.set('filter', params.filter);
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    return this.createTeamUrl(`dashboard/feed?${query.toString()}`);
  }

  public getAceCalendarEvents(params: {
    fundId?: string;
    tenantId?: string;
    limit?: number;
    cursor?: string;
    types?: string[];
  } = {}): string {
    const query = new URLSearchParams();
    query.set('limit', String(params.limit ?? 100));
    if (params.fundId) query.set('fundId', params.fundId);
    if (params.tenantId) query.set('tenantId', params.tenantId);
    if (params.cursor) query.set('cursor', params.cursor);
    params.types?.forEach(type => query.append('types', type));
    return this.createTeamUrl(`calendar/events?${query.toString()}`);
  }

  public getDashboardEventData(vehicleId: string): string {
    return this.createLpUrl(`vehicles/${vehicleId}/events?sort=start`, false);
  }
  public getDashboardNewsData(vehicleId: string): string {
    return this.createLpUrl(
      `vehicles/${vehicleId}/news?sort=dateTime&order=desc`,
      false
    );
  }
  public getDashboardUpdateData(vehicleId: string): string {
    return this.createLpUrl(
      `vehicles/${vehicleId}/updates?sort=date&order=desc`,
      false
    );
  }
}
